import React, { useMemo, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'

import { Loader } from '@/components/loaders/Loader'
import { Card } from '@/components/cards/Card'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import { Pill } from '@/components/pills/Pill'
import LineChart from '@/components/charts/LineChart'
import { nativeTooltip } from '@/components/charts/helpers'
import NoData from '@/components/NoData'
import { fetchScorecardOverview } from '@/reducers/qa-copilot/qa-copilot.actions'
import { chartColors } from '@/utils/constants'

import { useClassicScorecardSelection } from '../hooks/useClassicScorecardSelection'

const MAX_DEFAULT_SELECTED = 5

const GRANULARITY_OPTIONS = [
  { value: null, label: 'Auto' },
  { value: 'daily', label: 'Day' },
  { value: 'weekly', label: 'Week' },
  { value: 'monthly', label: 'Month' },
]

export const CriteriaTrendsWidget = () => {
  const dispatch = useDispatch()
  const {
    loading,
    data: { criteriaTrends },
    filters,
  } = useSelector((state) => state.qaCopilot)

  const [selectedCriteria, setSelectedCriteria] = useState({})
  const [selectedGranularity, setSelectedGranularity] = useState(null)

  // Source of truth: the scope tab's picker (new dashboard). On the
  // classic page there is no tab picker, so a local Select (stored in
  // redux so ScorecardPerformanceTable follows the same selection)
  // takes over; first scorecard is the last-resort default.
  const { selectedScorecard, selectedScorecardOption, scorecardSelect } =
    useClassicScorecardSelection('criteria-trends-select')

  // Reset the criterion pill selection when the underlying scorecard
  // changes -- otherwise a stale set of criteria_ids lingers and the
  // chart renders empty until the user re-toggles.
  useEffect(() => {
    setSelectedCriteria({})
  }, [selectedScorecard])

  useEffect(() => {
    if (selectedScorecard) {
      // One bundled fetch populates criteriaTrends + criteriaCorrelation +
      // scorecardPerformance. The other By-Scorecard widgets share these
      // redux keys, so they all light up from this single round trip.
      dispatch(fetchScorecardOverview(selectedScorecard, selectedGranularity))
    }
  }, [selectedScorecard, filters, selectedGranularity, dispatch])

  const availableCriteria = useMemo(() => {
    if (!criteriaTrends?.data) {
      return []
    }

    const seen = new Map()
    criteriaTrends.data.forEach((point) => {
      if (!seen.has(point.criteria_id)) {
        seen.set(point.criteria_id, point.criteria_name)
      }
    })

    return Array.from(seen, ([id, name]) => ({ id, name }))
  }, [criteriaTrends])

  useEffect(() => {
    if (!isEmpty(availableCriteria)) {
      setSelectedCriteria((prevSelected) => {
        if (!isEmpty(prevSelected)) {
          return prevSelected
        }
        const autoSelected = {}
        availableCriteria.slice(0, MAX_DEFAULT_SELECTED).forEach((criteria) => {
          autoSelected[criteria.id] = true
        })
        return autoSelected
      })
    }
  }, [availableCriteria])

  const toggleCriteria = (criteriaId) => {
    setSelectedCriteria((prev) => ({
      ...prev,
      [criteriaId]: !prev[criteriaId],
    }))
  }

  // Stable color map so each criteria keeps its color regardless of toggle state
  const criteriaColorMap = useMemo(() => {
    const map = {}
    availableCriteria.forEach((criteria, index) => {
      map[criteria.id] = chartColors[index % chartColors.length]
    })
    return map
  }, [availableCriteria])

  const chartData = useMemo(() => {
    if (!criteriaTrends?.data) {
      return []
    }

    const grouped = {}
    criteriaTrends.data.forEach((point) => {
      if (!selectedCriteria[point.criteria_id]) {
        return
      }

      if (!grouped[point.criteria_id]) {
        grouped[point.criteria_id] = {
          id: point.criteria_name,
          color: criteriaColorMap[point.criteria_id],
          data: [],
        }
      }
      grouped[point.criteria_id].data.push({
        x: point.date,
        y: Math.round(point.pass_rate * 10000) / 100,
      })
    })

    return Object.values(grouped)
  }, [criteriaTrends, selectedCriteria, criteriaColorMap])

  const colors = useMemo(() => chartData.map((series) => series.color), [chartData])

  const tickInterval = useMemo(() => {
    switch (criteriaTrends?.granularity) {
      case 'weekly':
        return 'every week'
      case 'monthly':
        return 'every month'
      default:
        return 'every day'
    }
  }, [criteriaTrends?.granularity])

  // Auto-scale y-axis so the data fills the chart area
  const { yMin, yMax } = useMemo(() => {
    let min = 100
    let max = 0
    chartData.forEach((series) => {
      series.data.forEach((dataPoint) => {
        if (dataPoint.y < min) {
          min = dataPoint.y
        }
        if (dataPoint.y > max) {
          max = dataPoint.y
        }
      })
    })
    const range = max - min
    const padding = Math.max(range * 0.2, 5)
    const flooredMin = Math.max(0, Math.floor((min - padding) / 5) * 5)
    const ceiledMax = Math.min(100, Math.ceil((max + padding) / 5) * 5)
    return { yMin: flooredMin, yMax: ceiledMax }
  }, [chartData])

  const granularityLabel = criteriaTrends?.granularity
    ? `Showing ${criteriaTrends.granularity} pass rates`
    : ''

  const hasSelectedCriteria = Object.values(selectedCriteria).some(Boolean)

  const renderChart = () => {
    if (loading.criteriaTrends) {
      return (
        <div data-testid="widget-loading" className="loading-container">
          <Loader />
        </div>
      )
    }

    if (!hasSelectedCriteria || isEmpty(chartData)) {
      return (
        <div className="empty-table">
          <NoData />
        </div>
      )
    }

    return (
      <div style={{ height: '364px' }}>
        <LineChart
          data={chartData}
          colors={colors}
          curve="monotoneX"
          enableGridX={false}
          enableGridY={false}
          pointSize={5}
          xScale={{
            type: 'time',
            format: '%Y-%m-%d',
            precision: 'day',
            useUTC: false,
          }}
          yScale={{ type: 'linear', min: yMin, max: yMax, stacked: false }}
          axisLeft={{
            orient: 'left',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Pass Rate',
            legendOffset: -60,
            legendPosition: 'middle',
            format: (value) => `${value}%`,
          }}
          axisBottom={{
            orient: 'bottom',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Date',
            legendOffset: 36,
            legendPosition: 'middle',
            tickValues: tickInterval,
            format: '%b %d',
          }}
          tooltip={nativeTooltip({
            trigger: 'axis',
            valueFormatter: (value) => `${Array.isArray(value) ? value[1] : value}%`,
          })}
        />
      </div>
    )
  }

  const cardTitle = (
    <>
      Criteria Trends
      {selectedScorecardOption?.label && (
        <span className="card-subtitle">· {selectedScorecardOption.label}</span>
      )}
    </>
  )

  return (
    <Card
      title={cardTitle}
      info="Each criterion's pass rate over time for the selected scorecard — spot criteria trending up or down."
      buttons={scorecardSelect}
      data-testid="criteria-trends-chart"
    >
      <div className="flex-space-between flex-align-center mb">
        <div data-testid="criteria-trends-granularity">
          <SegmentedControl
            small
            items={GRANULARITY_OPTIONS.map((option) => ({
              label: option.label,
              accessor: option.label,
              active: selectedGranularity === option.value,
              onClick: () => setSelectedGranularity(option.value),
            }))}
          />
        </div>
      </div>

      {granularityLabel}

      {/* Criteria legend/toggles live ABOVE the chart: inside a bento
          cell the card body scrolls, and a below-chart legend sat in
          the overflow where nobody saw it. */}
      {!isEmpty(availableCriteria) && (
        <div className="flex-center flex-wrap mt" style={{ gap: '8px' }}>
          {availableCriteria.map((criteria) => {
            const isChecked = !!selectedCriteria[criteria.id]
            return (
              <Pill
                key={criteria.id}
                dataTestId={`criteria-toggle-${criteria.id}`}
                small
                clickable
                selected={isChecked}
                transparent={!isChecked}
                gradient={isChecked ? criteriaColorMap[criteria.id] : undefined}
                onClick={() => toggleCriteria(criteria.id)}
              >
                {criteria.name}
              </Pill>
            )
          })}
        </div>
      )}

      {renderChart()}
    </Card>
  )
}
